import { getDeviceType, shouldShowMobileInterface } from './mobileDetection';

const GTM_ID = 'GTM-P4BMX67B';
const ANALYTICS_CONSENT_KEY = 'loopino.analytics_consent';
const ANALYTICS_CONSENT_TIMESTAMP_KEY = 'loopino.analytics_consent_ts';
const WEBSITE_COOKIE_PREFERENCES_KEY = 'cookie_preferences';
const WEBSITE_COOKIE_CONSENT_KEY = 'cookie_consent_given';
const WEBSITE_COOKIE_CATEGORY_COUNT = 3;
const WEBSITE_ANALYTICS_CATEGORY_INDEX = 1;
const ATTRIBUTION_STORAGE_KEY = 'loopino.analytics_attribution';
const ANALYTICS_HOST = 'loopino.ai';
const ANALYTICS_HOST_SUFFIX = '.loopino.ai';
const LANGUAGE_STORAGE_KEY = 'loopino.language';
const GA_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_gac_', '_gcl_au'];

const ATTRIBUTION_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
];

const BASE_PARAM_NAMES = ['surface', 'page_type', 'device_type', 'language', ...ATTRIBUTION_PARAM_NAMES];
const CTA_PARAM_NAMES = ['cta_label', 'cta_location', 'cta_destination'];
const FORM_PARAM_NAMES = [
  'form_id',
  'form_type',
  'lead_source',
  'request_type',
  'service_interest',
  'step_id',
  'step_index',
  'contact_method',
  'preferred_contact',
  'notification_status',
  'error_type',
];
const PAGE_PARAM_NAMES = ['page_location', 'page_path', 'page_title', 'screen_name', 'screen_class', 'consent_source'];
const AUTH_PARAM_NAMES = ['method', 'error_type'];
const MEDIA_PARAM_NAMES = ['entity', 'file_count', 'logo_count', 'brand_image_count', 'document_count', 'file_type', 'url_count', 'total_items'];
const COMPANY_PARAM_NAMES = ['analysis_type', 'include_website', 'include_documents', 'include_images'];
const AI_PARAM_NAMES = ['ai_task', 'image_count', 'image_size', 'image_quality', 'include_company_analysis', 'post_count', 'content_type'];
const OFFLINE_PARAM_NAMES = [
  'duration_ms',
  'dropped_count',
  'deleted_indexeddb_count',
  'offline_mutations_supported',
  'legacy_queue_dropped_count',
  'invalidation_reason',
  'action_type',
  'blocked_reason',
  'pending_actions',
  'online',
];
const SUBSCRIPTION_PARAM_NAMES = ['plan_code'];
const PWA_PARAM_NAMES = ['platform'];

const ALLOWED_PARAMS_BY_EVENT = {
  page_view: new Set([...BASE_PARAM_NAMES, ...PAGE_PARAM_NAMES]),
  screen_view: new Set([...BASE_PARAM_NAMES, 'screen_name', 'screen_class']),
  cta_click: new Set([...BASE_PARAM_NAMES, ...CTA_PARAM_NAMES]),
  form_start: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES, 'cta_label']),
  form_step_view: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES]),
  form_submit_start: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES]),
  generate_lead: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES]),
  form_submit_success: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES]),
  form_submit_error: new Set([...BASE_PARAM_NAMES, ...FORM_PARAM_NAMES]),
  consent_granted: new Set([...BASE_PARAM_NAMES, 'consent_source']),
  login: new Set([...BASE_PARAM_NAMES, ...AUTH_PARAM_NAMES]),
  login_error: new Set([...BASE_PARAM_NAMES, ...AUTH_PARAM_NAMES]),
  sign_up: new Set([...BASE_PARAM_NAMES, ...AUTH_PARAM_NAMES]),
  sign_up_error: new Set([...BASE_PARAM_NAMES, ...AUTH_PARAM_NAMES]),
  logout: new Set(BASE_PARAM_NAMES),
  verify_email: new Set(BASE_PARAM_NAMES),
  company_create: new Set(BASE_PARAM_NAMES),
  media_upload: new Set([...BASE_PARAM_NAMES, ...MEDIA_PARAM_NAMES]),
  company_analyze: new Set([...BASE_PARAM_NAMES, ...COMPANY_PARAM_NAMES]),
  company_dna_generate: new Set([...BASE_PARAM_NAMES, ...COMPANY_PARAM_NAMES]),
  ai_generate: new Set([...BASE_PARAM_NAMES, ...AI_PARAM_NAMES]),
  campaign_create: new Set([...BASE_PARAM_NAMES, 'has_files']),
  strategy_regenerate: new Set([...BASE_PARAM_NAMES, 'week_index']),
  post_approve: new Set(BASE_PARAM_NAMES),
  campaign_posts_generate: new Set([...BASE_PARAM_NAMES, 'generated_count']),
  post_regenerate: new Set([...BASE_PARAM_NAMES, 'regen_type']),
  offline_runtime_recovered: new Set([...BASE_PARAM_NAMES, ...OFFLINE_PARAM_NAMES]),
  offline_legacy_queue_dropped: new Set([...BASE_PARAM_NAMES, ...OFFLINE_PARAM_NAMES]),
  offline_mutation_blocked: new Set([...BASE_PARAM_NAMES, ...OFFLINE_PARAM_NAMES]),
  offline_mode: new Set([...BASE_PARAM_NAMES, ...OFFLINE_PARAM_NAMES]),
  pwa_install_prompt: new Set([...BASE_PARAM_NAMES, ...PWA_PARAM_NAMES]),
  pwa_install_guide_open: new Set([...BASE_PARAM_NAMES, ...PWA_PARAM_NAMES]),
  pwa_install_accept: new Set([...BASE_PARAM_NAMES, ...PWA_PARAM_NAMES]),
  pwa_install_dismiss: new Set([...BASE_PARAM_NAMES, ...PWA_PARAM_NAMES]),
  pwa_update_available: new Set(BASE_PARAM_NAMES),
  pwa_update_accept: new Set(BASE_PARAM_NAMES),
  pwa_update_dismiss: new Set(BASE_PARAM_NAMES),
  subscription_checkout_success: new Set(BASE_PARAM_NAMES),
  subscription_checkout_cancel: new Set(BASE_PARAM_NAMES),
  subscription_checkout_free: new Set(BASE_PARAM_NAMES),
  subscription_checkout_start: new Set([...BASE_PARAM_NAMES, ...SUBSCRIPTION_PARAM_NAMES]),
  subscription_change_now: new Set([...BASE_PARAM_NAMES, ...SUBSCRIPTION_PARAM_NAMES]),
  subscription_change_schedule: new Set([...BASE_PARAM_NAMES, ...SUBSCRIPTION_PARAM_NAMES]),
  subscription_portal_open: new Set(BASE_PARAM_NAMES),
};

const ALLOWED_EVENTS = new Set(Object.keys(ALLOWED_PARAMS_BY_EVENT));
const SAFE_IDENTIFIER_PARAMS = new Set(['form_id', 'step_id', 'utm_id']);

let gtmLoadStarted = false;
let currentConsentStatus = 'unknown';
let lastPageViewKey = '';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export const isAnalyticsEnabled = () => {
  if (process.env.NODE_ENV !== 'production') return false;
  if (!isBrowser()) return false;

  const hostname = window.location?.hostname || '';
  if (!hostname) return false;

  return hostname === ANALYTICS_HOST || hostname.endsWith(ANALYTICS_HOST_SUFFIX);
};

const ensureDataLayer = () => {
  if (!isBrowser()) return false;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  return true;
};

const safeParseJson = (value, fallback = null) => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

const safeGetItem = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const safeSetItem = (storage, key, value) => {
  try {
    storage.setItem(key, value);
    return true;
  } catch (_error) {
    return false;
  }
};

const safeRemoveItem = (storage, key) => {
  try {
    storage.removeItem(key);
    return true;
  } catch (_error) {
    return false;
  }
};

const normalizeString = (value, maxLength = 160) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const normalizeParamName = (value) => normalizeString(value, 80).toLowerCase();

const looksSensitiveValue = (value) => {
  const normalizedValue = normalizeString(value, 240);
  if (!normalizedValue) return false;
  return /[^\s@]+@[^\s@]+\.[^\s@]+/.test(normalizedValue);
};

const buildSafeSearch = (searchParams, { preserveAttribution = true } = {}) => {
  const safeParams = new URLSearchParams();
  if (!preserveAttribution) return '';

  ATTRIBUTION_PARAM_NAMES.forEach((name) => {
    const value = normalizeString(searchParams.get(name), 240);
    if (value && !looksSensitiveValue(value)) {
      safeParams.set(name, value);
    }
  });

  const serialized = safeParams.toString();
  return serialized ? `?${serialized}` : '';
};

const redactUrl = (value, { absolute = true, preserveAttribution = false } = {}) => {
  if (!isBrowser()) return '';

  const rawValue = normalizeString(value, 500);
  if (!rawValue) return '';
  if (rawValue.startsWith('#')) return rawValue.slice(0, 120);

  try {
    const url = new URL(rawValue, window.location.origin);
    const safeSearch = buildSafeSearch(url.searchParams, { preserveAttribution });
    if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
      return url.protocol.replace(':', '');
    }
    if (!absolute && url.origin === window.location.origin) {
      return `${url.pathname}${safeSearch}`;
    }
    return `${url.origin}${url.pathname}${safeSearch}`;
  } catch (_error) {
    return rawValue.split('?')[0].split('#')[0].slice(0, 240);
  }
};

const readWebsiteConsentStatus = () => {
  if (!isBrowser()) return 'unknown';

  const consentGiven = safeGetItem(window.localStorage, WEBSITE_COOKIE_CONSENT_KEY) === 'true';
  if (!consentGiven) return 'unknown';

  const preferences = safeParseJson(safeGetItem(window.localStorage, WEBSITE_COOKIE_PREFERENCES_KEY) || '[]', []);
  if (!Array.isArray(preferences)) return 'unknown';

  return preferences[WEBSITE_ANALYTICS_CATEGORY_INDEX] === true ? 'granted' : 'denied';
};

const getWebsitePreferencesSnapshot = () => {
  if (!isBrowser()) return [true, false, false];

  const storedPreferences = safeParseJson(
    safeGetItem(window.localStorage, WEBSITE_COOKIE_PREFERENCES_KEY) || '[]',
    []
  );

  if (!Array.isArray(storedPreferences)) {
    return [true, false, false];
  }

  const normalized = storedPreferences.map((value) => Boolean(value));
  if (normalized.length < WEBSITE_COOKIE_CATEGORY_COUNT) {
    while (normalized.length < WEBSITE_COOKIE_CATEGORY_COUNT) {
      normalized.push(false);
    }
  }

  normalized[0] = true;
  return normalized;
};

const readPlatformConsentStatus = () => {
  if (!isBrowser()) return 'unknown';

  const stored = safeGetItem(window.localStorage, ANALYTICS_CONSENT_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : 'unknown';
};

const resolveStoredConsentStatus = () => {
  const websiteConsent = readWebsiteConsentStatus();
  if (websiteConsent !== 'unknown') {
    return websiteConsent;
  }

  return readPlatformConsentStatus();
};

const writeWebsiteConsentStatus = (status) => {
  if (!isBrowser()) return;

  const analyticsGranted = status === 'granted';
  const preferences = getWebsitePreferencesSnapshot();
  preferences[WEBSITE_ANALYTICS_CATEGORY_INDEX] = analyticsGranted;
  safeSetItem(window.localStorage, WEBSITE_COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
  safeSetItem(window.localStorage, WEBSITE_COOKIE_CONSENT_KEY, 'true');
};

const writePlatformConsentStatus = (status) => {
  if (!isBrowser()) return;

  const resolved = status === 'granted' ? 'granted' : 'denied';
  safeSetItem(window.localStorage, ANALYTICS_CONSENT_KEY, resolved);
  safeSetItem(window.localStorage, ANALYTICS_CONSENT_TIMESTAMP_KEY, Date.now().toString());
};

const syncConsentStorage = (status) => {
  if (status !== 'granted' && status !== 'denied') return;
  writeWebsiteConsentStatus(status);
  writePlatformConsentStatus(status);
};

const applyConsent = (status) => {
  if (!isAnalyticsEnabled()) return;
  if (!ensureDataLayer()) return;

  const granted = status === 'granted';

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (granted) {
    loadGtm();
  }
};

const deleteCookie = (name, domain = '') => {
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax`;
};

const deleteGaCookies = () => {
  if (!isBrowser()) return;

  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => GA_COOKIE_PREFIXES.some((prefix) => name === prefix || name.startsWith(prefix)));

  if (!cookieNames.length) return;

  const hostname = window.location.hostname || '';
  const domains = Array.from(
    new Set(['', hostname, hostname ? `.${hostname}` : '', ANALYTICS_HOST, `.${ANALYTICS_HOST}`].filter(Boolean))
  );

  cookieNames.forEach((name) => {
    domains.forEach((domain) => deleteCookie(name, domain));
  });
};

const loadGtm = () => {
  if (!isAnalyticsEnabled() || gtmLoadStarted) return false;
  if (!ensureDataLayer()) return false;

  gtmLoadStarted = true;
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const firstScript = document.getElementsByTagName('script')[0];
  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  } else {
    document.head.append(gtmScript);
  }

  return true;
};

const resolveLanguage = () => {
  if (!isBrowser()) return null;
  return safeGetItem(window.localStorage, LANGUAGE_STORAGE_KEY) || window.navigator?.language || null;
};

const getPageType = () => {
  if (!isBrowser()) return 'app';

  const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';

  if (path === '/' || path === '/landing') return 'landing';
  if (path === '/verify-email' || path === '/auth/callback') return 'auth';
  if (path === '/privacy-policy' || path === '/cookie-policy' || path === '/termini' || path === '/cookies') {
    return 'legal';
  }
  return 'app';
};

const getCurrentAttribution = () => {
  if (!isBrowser()) return {};

  const params = new URLSearchParams(window.location.search || '');
  const values = {};
  ATTRIBUTION_PARAM_NAMES.forEach((name) => {
    const value = normalizeString(params.get(name), 240);
    if (value && !looksSensitiveValue(value)) {
      values[name] = value;
    }
  });
  return values;
};

const captureAttribution = () => {
  if (!isBrowser() || currentConsentStatus !== 'granted') return;

  const currentAttribution = getCurrentAttribution();
  if (!Object.keys(currentAttribution).length) return;

  safeSetItem(
    window.sessionStorage,
    ATTRIBUTION_STORAGE_KEY,
    JSON.stringify({
      captured_at: new Date().toISOString(),
      landing_page: redactUrl(window.location.href, { absolute: false, preserveAttribution: false }),
      ...currentAttribution,
    })
  );
};

const readStoredAttribution = () => {
  if (!isBrowser()) return {};

  const parsed = safeParseJson(safeGetItem(window.sessionStorage, ATTRIBUTION_STORAGE_KEY) || '{}', {});
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  return Object.fromEntries(
    Object.entries(parsed)
      .filter(([key]) => ATTRIBUTION_PARAM_NAMES.includes(key))
      .map(([key, value]) => [key, normalizeString(value, 240)])
      .filter(([, value]) => value)
  );
};

const clearStoredAttribution = () => {
  if (!isBrowser()) return;
  safeRemoveItem(window.sessionStorage, ATTRIBUTION_STORAGE_KEY);
};

const getAttribution = () => ({
  ...(currentConsentStatus === 'granted' ? readStoredAttribution() : {}),
  ...(currentConsentStatus === 'granted' ? getCurrentAttribution() : {}),
});

const getBaseContext = () => {
  if (!isBrowser()) return {};

  const language = resolveLanguage();
  const isMobileSurface = shouldShowMobileInterface();

  return {
    surface: isMobileSurface ? 'mobile' : 'desktop',
    page_type: getPageType(),
    device_type: getDeviceType(),
    language: language || undefined,
    ...(currentConsentStatus === 'granted' ? getAttribution() : {}),
  };
};

const isDisallowedIdentifierParam = (key) => {
  if (!key) return false;
  if (SAFE_IDENTIFIER_PARAMS.has(key)) return false;
  return key === 'id' || key.endsWith('_id');
};

const sanitizeValue = (key, value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (key === 'page_location') return redactUrl(value, { absolute: true, preserveAttribution: false });
  if (key === 'page_path') return redactUrl(value, { absolute: false, preserveAttribution: false });
  if (key === 'cta_destination') return redactUrl(value, { absolute: false, preserveAttribution: false });
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') return normalizeString(value, 240);
  return undefined;
};

const sanitizeParams = (eventName, params = {}) => {
  const allowedParams = ALLOWED_PARAMS_BY_EVENT[eventName];
  if (!allowedParams || !params || typeof params !== 'object' || Array.isArray(params)) return {};

  const sanitized = {};
  Object.entries(params).forEach(([key, value]) => {
    const normalizedKey = normalizeParamName(key);
    if (!normalizedKey) return;
    if (isDisallowedIdentifierParam(normalizedKey)) return;
    if (!allowedParams.has(normalizedKey)) return;

    const sanitizedValue = sanitizeValue(normalizedKey, value);
    if (sanitizedValue !== undefined && sanitizedValue !== '') {
      sanitized[normalizedKey] = sanitizedValue;
    }
  });

  return sanitized;
};

export const getConsentStatus = () => {
  if (!isBrowser()) return 'denied';

  const resolved = resolveStoredConsentStatus();
  if (resolved !== 'unknown') {
    currentConsentStatus = resolved;
    return resolved;
  }

  return currentConsentStatus;
};

export const setConsentStatus = (status, options = {}) => {
  if (!isBrowser()) return false;

  const resolved = status === 'granted' ? 'granted' : 'denied';
  const previousConsentStatus = getConsentStatus();

  currentConsentStatus = resolved;
  syncConsentStorage(resolved);
  applyConsent(resolved);

  if (resolved !== 'granted') {
    clearStoredAttribution();
    lastPageViewKey = '';
    deleteGaCookies();
    return false;
  }

  captureAttribution();
  if (previousConsentStatus !== 'granted' && options.trackPageView !== false) {
    trackPageView({ consent_source: options.source || 'app_banner' });
  }
  return true;
};

export const initAnalytics = () => {
  if (!isAnalyticsEnabled()) return false;
  if (!ensureDataLayer()) return false;

  currentConsentStatus = resolveStoredConsentStatus();
  applyConsent(currentConsentStatus === 'granted' ? 'granted' : 'denied');

  if (currentConsentStatus === 'granted' || currentConsentStatus === 'denied') {
    syncConsentStorage(currentConsentStatus);
  }

  if (currentConsentStatus === 'granted') {
    captureAttribution();
  }

  return true;
};

export const trackEvent = (eventName, params = {}) => {
  if (!isAnalyticsEnabled()) return false;
  if (getConsentStatus() !== 'granted') return false;
  if (!ensureDataLayer()) return false;

  const normalizedEventName = normalizeParamName(eventName);
  if (!ALLOWED_EVENTS.has(normalizedEventName)) return false;

  const payload = {
    event: normalizedEventName,
    ...sanitizeParams(normalizedEventName, {
      ...getBaseContext(),
      ...params,
    }),
  };

  loadGtm();
  window.dataLayer.push(payload);
  return true;
};

export const trackPageView = (params = {}) => {
  if (!isBrowser()) return false;

  const location = redactUrl(window.location?.href || '', { absolute: true, preserveAttribution: false });
  const path = redactUrl(window.location?.href || '', { absolute: false, preserveAttribution: false });
  const title = document?.title || 'Loopino';
  const screenName = normalizeString(params?.screen_name || '', 160);
  const pageViewKey = `${location}|${title}|${screenName}`;

  if (pageViewKey === lastPageViewKey) return false;
  const wasTracked = trackEvent('page_view', {
    page_location: location,
    page_path: path,
    page_title: title,
    ...params,
  });

  if (wasTracked) {
    lastPageViewKey = pageViewKey;
  }

  return wasTracked;
};

export const trackScreenView = (screenName, params = {}) => {
  if (!screenName) return false;
  return trackEvent('screen_view', {
    screen_name: screenName,
    screen_class: screenName,
    ...params,
  });
};
